import { create } from "zustand";
import { admin, type AdminUserRow, type InviteRow, type ListUsersParams, type UserSortKey } from "../lib/api";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

interface InvitesSlice {
  invitesStatus: LoadStatus;
  invitesError: string | null;
  invites: InviteRow[];
  invitesPending: boolean;

  loadInvites: () => Promise<void>;
  createInvite: (name: string, maxUses?: number | null) => Promise<InviteRow>;
  expireInvite: (code: string) => Promise<void>;
  revokeInvite: (code: string) => Promise<void>;
}

interface UsersSlice {
  usersStatus: LoadStatus;
  usersError: string | null;
  users: AdminUserRow[];
  total: number;
  page: number;
  pageSize: number;
  sort: UserSortKey;
  sortDir: "asc" | "desc";
  q: string;
  inviteCode: string;
  usersPending: boolean;

  loadUsers: () => Promise<void>;
  setUsersQuery: (patch: Partial<{ page: number; pageSize: number; sort: UserSortKey; sortDir: "asc" | "desc"; q: string; inviteCode: string }>) => Promise<void>;
  toggleSort: (key: UserSortKey) => Promise<void>;
  blockUser: (id: string) => Promise<void>;
  unblockUser: (id: string) => Promise<void>;
  softDeleteUser: (id: string) => Promise<void>;
}

export type AdminState = InvitesSlice & UsersSlice;

function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

const DEFAULT_PAGE_SIZE = 25;

export const useAdminStore = create<AdminState>((set, get) => ({
  // ─── Invites ──────────────────────────────────────────────────────────
  invitesStatus: "idle",
  invitesError: null,
  invites: [],
  invitesPending: false,

  async loadInvites() {
    set({ invitesStatus: "loading", invitesError: null });
    try {
      const { invites } = await admin.invites.list();
      set({ invitesStatus: "ready", invites });
    } catch (err) {
      set({ invitesStatus: "error", invitesError: errMessage(err) });
    }
  },

  async createInvite(name, maxUses) {
    set({ invitesPending: true, invitesError: null });
    try {
      const { invite } = await admin.invites.create(name, maxUses);
      set((s) => ({ invites: [invite, ...s.invites], invitesPending: false }));
      return invite;
    } catch (err) {
      set({ invitesPending: false, invitesError: errMessage(err) });
      throw err;
    }
  },

  async expireInvite(code) {
    set({ invitesPending: true, invitesError: null });
    try {
      await admin.invites.expire(code);
      // Refetch so the expires_at update is reflected canonically.
      const { invites } = await admin.invites.list();
      set({ invites, invitesPending: false });
    } catch (err) {
      set({ invitesPending: false, invitesError: errMessage(err) });
      throw err;
    }
  },

  async revokeInvite(code) {
    set({ invitesPending: true, invitesError: null });
    try {
      await admin.invites.revoke(code);
      set((s) => ({
        invites: s.invites.filter((i) => i.code !== code),
        invitesPending: false,
      }));
    } catch (err) {
      set({ invitesPending: false, invitesError: errMessage(err) });
      throw err;
    }
  },

  // ─── Users ────────────────────────────────────────────────────────────
  usersStatus: "idle",
  usersError: null,
  users: [],
  total: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sort: "created_at",
  sortDir: "desc",
  q: "",
  inviteCode: "",
  usersPending: false,

  async loadUsers() {
    const s = get();
    set({ usersStatus: "loading", usersError: null });
    try {
      const params: ListUsersParams = {
        page: s.page,
        pageSize: s.pageSize,
        sort: s.sort,
        sortDir: s.sortDir,
      };
      if (s.q.trim()) params.q = s.q.trim();
      if (s.inviteCode) params.inviteCode = s.inviteCode;
      const page = await admin.users.list(params);
      set({
        usersStatus: "ready",
        users: page.users,
        total: page.total,
        page: page.page,
        pageSize: page.pageSize,
      });
    } catch (err) {
      set({ usersStatus: "error", usersError: errMessage(err) });
    }
  },

  async setUsersQuery(patch) {
    // Any filter/search/sort change resets to page 1; explicit page changes pass through.
    const explicitPage = patch.page !== undefined;
    set({ ...patch, ...(explicitPage ? {} : { page: 1 }) });
    await get().loadUsers();
  },

  async toggleSort(key) {
    const s = get();
    if (s.sort === key) {
      await get().setUsersQuery({ sortDir: s.sortDir === "asc" ? "desc" : "asc" });
    } else {
      await get().setUsersQuery({ sort: key, sortDir: "desc" });
    }
  },

  async blockUser(id) {
    set({ usersPending: true, usersError: null });
    try {
      await admin.users.block(id);
      set((s) => ({
        users: s.users.map((u) => (u.id === id ? { ...u, status: "blocked" } : u)),
        usersPending: false,
      }));
    } catch (err) {
      set({ usersPending: false, usersError: errMessage(err) });
      throw err;
    }
  },

  async unblockUser(id) {
    set({ usersPending: true, usersError: null });
    try {
      await admin.users.unblock(id);
      set((s) => ({
        users: s.users.map((u) => (u.id === id ? { ...u, status: "active" } : u)),
        usersPending: false,
      }));
    } catch (err) {
      set({ usersPending: false, usersError: errMessage(err) });
      throw err;
    }
  },

  async softDeleteUser(id) {
    set({ usersPending: true, usersError: null });
    try {
      await admin.users.softDelete(id);
      // Refetch — total + page boundaries shift.
      set({ usersPending: false });
      await get().loadUsers();
    } catch (err) {
      set({ usersPending: false, usersError: errMessage(err) });
      throw err;
    }
  },
}));
